<script>
import { defineAsyncComponent } from 'vue';
import emojis from './emojisGroup.json';
import FluentIcon from 'shared/components/FluentIcon/Index.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import {
  getRecentReactions,
  rememberRecentReaction,
} from 'shared/helpers/recentReactions';
// A biblioteca de figurinhas só é necessária quando a aba dela é aberta.
const GifPicker = defineAsyncComponent(() => import('./GifPicker.vue'));

const SEARCH_KEY = 'Busca';
const RECENT_REACTIONS_KEY = 'Reações recentes';
const CATEGORY_LABELS = {
  'Smileys & Emotion': 'Sorrisos e emoções',
  'People & Body': 'Pessoas e corpo',
  'Animals & Nature': 'Animais e natureza',
  'Food & Drink': 'Comidas e bebidas',
  'Travel & Places': 'Viagens e lugares',
  Activities: 'Atividades',
  Objects: 'Objetos',
  Symbols: 'Símbolos',
  Flags: 'Bandeiras',
};
const CATEGORY_ICONS = {
  'Smileys & Emotion': 'i-lucide-smile',
  'People & Body': 'i-lucide-user-round',
  'Animals & Nature': 'i-lucide-paw-print',
  'Food & Drink': 'i-lucide-apple',
  'Travel & Places': 'i-lucide-car-front',
  Activities: 'i-lucide-volleyball',
  Objects: 'i-lucide-lightbulb',
  Symbols: 'i-lucide-heart',
  Flags: 'i-lucide-flag',
};

export default {
  components: { FluentIcon, NextButton, GifPicker },
  props: {
    onClick: {
      type: Function,
      default: () => {},
    },
    showRemoveButton: {
      type: Boolean,
      default: false,
    },
    // Modo opt-in (Aceleres): renderiza TODAS as categorias num único scroll
    // contínuo (estilo WhatsApp), sincroniza a aba ativa com a categoria
    // visível e faz smooth-scroll quando clica numa aba. Default false
    // mantém o comportamento original (uma categoria por vez).
    scrollSyncTabs: {
      type: Boolean,
      default: false,
    },
    progressiveCategories: {
      type: Boolean,
      default: false,
    },
    // Callback da figurinha. Quando presente, aparece a aba própria no topo do
    // picker. No desktop, uma figurinha Lottie guardada também passa por ele.
    onGifSelect: {
      type: Function,
      default: null,
    },
    allowSvgUpload: {
      type: Boolean,
      default: false,
    },
    allowLottieStickers: {
      type: Boolean,
      default: false,
    },
    embedded: {
      type: Boolean,
      default: false,
    },
    focusSearchOnMount: {
      type: Boolean,
      default: true,
    },
    initialCategory: {
      type: String,
      default: '',
    },
    showSearchIcon: {
      type: Boolean,
      default: true,
    },
    showRecentReactions: {
      type: Boolean,
      default: false,
    },
    // Aceleres: folha de emojis do telemovel no layout do WhatsApp -- lupa a
    // esquerda no topo (a busca so aparece ao toca-la), abas ao centro, botao
    // de apagar a direita e categorias no rodape. So a instancia mobile do
    // ReplyBox liga isto; o `embedded` do computador continua igual.
    mobileSheet: {
      type: Boolean,
      default: false,
    },
    onBackspace: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      selectedKey: this.progressiveCategories
        ? this.initialCategory || emojis[0].name
        : this.showRecentReactions
          ? RECENT_REACTIONS_KEY
          : this.initialCategory || SEARCH_KEY,
      skipSearchReset: false,
      emojis,
      recentReactionsKey: RECENT_REACTIONS_KEY,
      recentReactions: [],
      search: '',
      suppressScrollSync: false,
      activeTab: 'emoji',
      mobileSearchOpen: false,
    };
  },
  computed: {
    // Na folha do telemovel a cor segue os tokens: dentro do `acl-light-scope`
    // o `dark:` do Tailwind deixaria o icone ativo branco sobre fundo claro.
    activeCategoryIconClass() {
      return this.mobileSheet
        ? '!text-n-slate-12'
        : '!text-black dark:!text-white';
    },
    categories() {
      return [...this.emojis];
    },
    filterEmojisByCategory() {
      const selectedCategoryName = this.emojis.find(category =>
        category.name === this.selectedKey ? category.name : null
      );
      return selectedCategoryName?.emojis;
    },
    filterAllEmojisBySearch() {
      return this.emojis.map(category => {
        const allEmojis = category.emojis.filter(emoji =>
          emoji.slug.replaceAll('_', ' ').includes(this.search.toLowerCase())
        );
        return allEmojis.length > 0
          ? { ...category, emojis: allEmojis }
          : { ...category, emojis: [] };
      });
    },
    renderedSections() {
      if (!this.progressiveCategories || this.search) {
        return this.filterAllEmojisBySearch;
      }
      if (this.selectedKey === RECENT_REACTIONS_KEY) return [];
      return this.emojis.filter(category => category.name === this.selectedKey);
    },
    hasNoSearch() {
      return this.selectedKey !== SEARCH_KEY && this.search === '';
    },
    hasEmptySearchResult() {
      return this.filterAllEmojisBySearch.every(
        category => category.emojis.length === 0
      );
    },
  },
  watch: {
    search() {
      if (this.skipSearchReset) {
        this.skipSearchReset = false;
        return;
      }
      if (!this.progressiveCategories) {
        if (this.search) this.selectedKey = SEARCH_KEY;
        return;
      }
      this.selectedKey = this.search ? SEARCH_KEY : this.emojis[0].name;
      this.$refs.emojiItem?.scrollTo({ top: 0 });
    },
    selectedKey() {
      return this.selectedKey === SEARCH_KEY ? this.focusSearchInput() : null;
    },
  },
  created() {
    this.sectionRefs = {};
  },
  mounted() {
    this.loadRecentReactions();
    if (this.focusSearchOnMount) this.focusSearchInput();
    if (this.scrollSyncTabs && !this.showRecentReactions) {
      this.$nextTick(() => {
        const first = this.categories[0]?.name;
        if (first) this.selectedKey = first;
      });
    }
  },
  methods: {
    loadRecentReactions() {
      if (!this.showRecentReactions) return;

      const availableReactions = this.emojis.flatMap(category =>
        category.emojis.map(emoji => emoji.emoji)
      );
      this.recentReactions = getRecentReactions(availableReactions);
    },
    selectEmoji(emoji) {
      if (this.showRecentReactions) {
        this.recentReactions = rememberRecentReaction(emoji);
      }
      this.onClick(emoji);
    },
    changeCategory(category) {
      if (this.progressiveCategories) {
        this.skipSearchReset = Boolean(this.search);
        this.search = '';
        this.selectedKey = category;
        this.$refs.emojiItem?.scrollTo({ top: 0 });
        return;
      }
      this.search = '';
      if (this.showRecentReactions && category === this.recentReactionsKey) {
        this.$nextTick(() => {
          this.selectedKey = category;
          this.$refs.emojiItem?.scrollTo({
            top: 0,
            behavior: this.scrollSyncTabs ? 'smooth' : 'auto',
          });
        });
        return;
      }
      if (
        (this.scrollSyncTabs || this.showRecentReactions) &&
        category !== SEARCH_KEY
      ) {
        this.selectedKey = category;
        this.scrollToCategory(category);
        return;
      }
      this.$refs.emojiItem?.scrollTo({ top: 0 });
      this.selectedKey = category;
    },
    assignSectionRef(name, el) {
      if (el) this.sectionRefs[name] = el;
      else delete this.sectionRefs[name];
    },
    getSectionScrollTop(container, el) {
      let top = 0;
      let current = el;

      while (current && current !== container) {
        top += current.offsetTop;
        current = current.offsetParent;
      }

      return top;
    },
    scrollToCategory(name) {
      this.$nextTick(() => {
        const container = this.$refs.emojiItem;
        const el = this.sectionRefs[name];
        if (!container || !el) return;
        this.suppressScrollSync = true;
        const requestedTop = this.getSectionScrollTop(container, el) - 4;
        const maxScrollTop = Math.max(
          0,
          container.scrollHeight - container.clientHeight
        );
        const targetTop = Math.min(maxScrollTop, Math.max(0, requestedTop));
        container.scrollTo({
          top: targetTop,
          behavior: this.scrollSyncTabs ? 'smooth' : 'auto',
        });
        if (this.suppressScrollSyncTimer) {
          clearTimeout(this.suppressScrollSyncTimer);
        }
        this.suppressScrollSyncTimer = setTimeout(() => {
          this.suppressScrollSync = false;
        }, 450);
      });
    },
    onEmojiScroll() {
      if (this.progressiveCategories) return;
      if (
        (!this.scrollSyncTabs && !this.showRecentReactions) ||
        this.suppressScrollSync
      )
        return;
      const container = this.$refs.emojiItem;
      if (!container) return;
      const containerTop = container.scrollTop + 12;
      const firstCategoryName = this.categories[0]?.name;
      const firstCategory = this.sectionRefs[firstCategoryName];
      const firstCategoryTop = firstCategory
        ? this.getSectionScrollTop(container, firstCategory)
        : 0;
      let activeName = this.categories[0]?.name;

      if (
        this.showRecentReactions &&
        !this.search &&
        firstCategoryTop > containerTop
      ) {
        activeName = this.recentReactionsKey;
      } else {
        for (const cat of this.categories) {
          const el = this.sectionRefs[cat.name];
          if (!el) continue;
          if (this.getSectionScrollTop(container, el) <= containerTop) {
            activeName = cat.name;
          } else break;
        }
      }
      if (activeName && this.selectedKey !== activeName) {
        this.selectedKey = activeName;
      }
    },
    getFirstEmojiByCategoryName(categoryName) {
      const categoryItem = this.emojis.find(category =>
        category.name === categoryName ? category : null
      );
      return categoryItem ? categoryItem.emojis[0].emoji : '';
    },
    getCategoryLabel(categoryName) {
      return CATEGORY_LABELS[categoryName] || categoryName;
    },
    getCategoryIcon(categoryName) {
      return CATEGORY_ICONS[categoryName] || 'i-lucide-smile';
    },
    toggleMobileSearch() {
      this.mobileSearchOpen = !this.mobileSearchOpen;
      if (!this.mobileSearchOpen) {
        this.search = '';
        return;
      }
      this.activeTab = 'emoji';
      this.focusSearchInput();
    },
    focusSearchInput() {
      this.$nextTick(() => {
        this.$refs.searchbar.focus();
      });
    },
  },
};
</script>

<template>
  <div
    role="dialog"
    class="emoji-dialog bg-n-background shadow-lg rounded-md outline outline-1 outline-n-weak box-content h-[18.75rem] absolute right-0 -top-[95px] w-80 z-20"
    :class="{
      'emoji-dialog--with-gif': !!onGifSelect,
      '!static !z-auto !h-full !w-full !max-h-none !rounded-none !border-0 !bg-transparent !shadow-none !outline-none before:!hidden': embedded,
    }"
  >
    <div class="flex flex-col h-full">
      <!-- Aceleres 2026-09-14: na folha do telemovel a barra de cima tambem
           aparece sem GIFs (a lupa e o unico acesso a busca nesse layout).
           As abas so com GIFs e o apagar so com `onBackspace`: a folha de
           reacoes da galeria nao tem nenhum dos dois; a da conversa tem ambos. -->
      <div
        v-if="onGifSelect || mobileSheet"
        class="emoji-dialog--top-tabs flex border-b border-n-weak"
        :class="{
          '!sticky !top-0 !z-30 !bg-[#f7f8fa] dark:!bg-n-solid-2': embedded,
          '!items-center !gap-2 !border-b-0 !px-4 !py-1.5': mobileSheet,
        }"
        role="tablist"
      >
        <button
          v-if="mobileSheet"
          type="button"
          class="flex size-10 flex-shrink-0 items-center justify-center rounded-full border-0 bg-transparent p-0 text-n-slate-11"
          :class="{ '!bg-n-alpha-2': mobileSearchOpen }"
          aria-label="Buscar emojis"
          @click="toggleMobileSearch"
        >
          <i class="i-lucide-search !size-7" aria-hidden="true" />
        </button>
        <!-- Aceleres 2026-09-15: sem GIFs (folha de reacoes) a pilula fica so com
             a carita, centrada entre a lupa e o apagar. -->
        <div
          v-if="onGifSelect || mobileSheet"
          :class="
            mobileSheet
              ? [
                  'mx-auto flex h-10 overflow-hidden rounded-full border border-n-weak',
                  onGifSelect ? 'w-36' : 'w-20',
                ]
              : 'contents'
          "
        >
        <button
          type="button"
          class="emoji-dialog--top-tab"
          :class="{
            'emoji-dialog--top-tab--active': activeTab === 'emoji',
            '!h-full !border-b-0 !py-0 !text-n-slate-10': mobileSheet,
            '!bg-n-alpha-2 !text-n-slate-12': mobileSheet && activeTab === 'emoji',
          }"
          role="tab"
          :aria-selected="activeTab === 'emoji'"
          @click="activeTab = 'emoji'"
        >
          <i
            v-if="mobileSheet"
            class="i-lucide-smile !size-6"
            aria-label="Emojis"
          />
          <template v-else>
            <FluentIcon icon="emoji" size="18" />
            <span>Emojis</span>
          </template>
        </button>
        <button
          v-if="onGifSelect"
          type="button"
          class="emoji-dialog--top-tab"
          :class="{
            'emoji-dialog--top-tab--active': activeTab === 'gif',
            '!h-full !border-b-0 !py-0 !text-n-slate-10': mobileSheet,
            '!bg-n-alpha-2 !text-n-slate-12': mobileSheet && activeTab === 'gif',
          }"
          role="tab"
          :aria-selected="activeTab === 'gif'"
          @click="activeTab = 'gif'"
        >
          <i
            v-if="mobileSheet"
            class="i-lucide-sticker !size-6"
            aria-label="Figurinhas"
          />
          <span v-else class="emoji-dialog--gif-badge">Figurinhas</span>
        </button>
        </div>
        <button
          v-if="mobileSheet && onBackspace"
          type="button"
          class="flex size-10 flex-shrink-0 items-center justify-center rounded-full border-0 bg-transparent p-0 text-n-slate-11"
          aria-label="Apagar"
          @click="onBackspace?.()"
        >
          <i class="i-lucide-delete !size-7" aria-hidden="true" />
        </button>
        <span
          v-else-if="mobileSheet"
          class="size-10 flex-shrink-0"
          aria-hidden="true"
        />
      </div>

      <GifPicker
        v-if="onGifSelect && activeTab === 'gif'"
        :on-select="onGifSelect"
        :allow-svg-upload="allowSvgUpload"
        :allow-lottie-stickers="allowLottieStickers"
        :mobile-sheet="mobileSheet"
        class="flex-1 min-h-0"
      />

      <template v-if="!onGifSelect || activeTab === 'emoji'">
      <div
        v-if="!mobileSheet || mobileSearchOpen"
        class="flex gap-2 m-2 sticky top-2"
        :class="{ '!sticky !top-0 !z-20 !mx-3 !mb-1 !mt-2 !bg-[#f7f8fa] dark:!bg-n-solid-2': embedded }"
      >
        <i
          v-if="embedded && showSearchIcon"
          class="i-lucide-search pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-n-slate-10"
          aria-hidden="true"
        />
        <input
          ref="searchbar"
          v-model="search"
          type="text"
          class="focus:box-shadow-blue dark:focus:box-shadow-dark !mb-0 !h-8 !text-sm"
          :class="{ '!h-10 !rounded-full !pl-9 !pr-3': embedded }"
          :placeholder="$t('EMOJI.PLACEHOLDER')"
        />
        <NextButton
          v-if="showRemoveButton"
          faded
          sm
          slate
          class="flex-shrink-0"
          :label="$t('EMOJI.REMOVE')"
          @click="onClick('')"
        />
      </div>
      <div
        v-if="scrollSyncTabs || showRecentReactions"
        ref="emojiItem"
        class="emoji-item emoji-item--continuous"
        :class="{ '!relative !z-0 !h-auto !min-h-0 !flex-1 !px-2 !pb-2': embedded }"
        @scroll.passive="onEmojiScroll"
      >
        <div
          v-if="
            showRecentReactions &&
            search === '' &&
            (!progressiveCategories || selectedKey === recentReactionsKey)
          "
          class="emoji--section"
        >
          <h5
            class="emoji--section-title text-sm text-n-slate-12 font-medium leading-normal m-0 py-1 px-2 capitalize"
            :class="{
              '!sticky !top-0 !z-10 !mt-2 !bg-[#f7f8fa] !px-1.5 !py-1.5 !text-[0.6875rem] !font-semibold !uppercase !tracking-wide dark:!bg-n-solid-2': embedded,
            }"
          >
            Recentes
          </h5>
          <div
            v-if="recentReactions.length"
            class="emoji--row"
            :class="{ '!grid !grid-cols-8 !gap-0.5 !p-0': embedded }"
          >
            <button
              v-for="reaction in recentReactions"
              :key="reaction"
              v-dompurify-html="reaction"
              v-twemoji
              class="emoji--item"
              :class="{
                '!m-0 !inline-flex !h-[2.375rem] !w-full !items-center !justify-center !rounded-md !text-2xl': embedded,
              }"
              type="button"
              :aria-label="`Reagir com ${reaction}`"
              @click="selectEmoji(reaction)"
            />
          </div>
          <div
            v-else
            class="items-center flex flex-col h-[13.25rem] justify-center"
          >
            <div class="text-n-slate-11 mb-2">
              <FluentIcon icon="history" size="48" />
            </div>
            <span class="text-n-slate-11 text-sm font-medium">
              Nenhuma reação recente
            </span>
          </div>
        </div>
        <div
          v-for="category in renderedSections"
          :key="category.slug"
          :ref="el => assignSectionRef(category.name, el)"
          class="emoji--section"
        >
          <h5
            v-if="category.emojis.length > 0"
            class="emoji--section-title text-sm text-n-slate-12 font-medium leading-normal m-0 py-1 px-2 capitalize"
            :class="{
              '!sticky !top-0 !z-10 !mt-2 !bg-[#f7f8fa] !px-1.5 !py-1.5 !text-[0.6875rem] !font-semibold !uppercase !tracking-wide dark:!bg-n-solid-2': embedded,
            }"
          >
            {{ getCategoryLabel(category.name) }}
          </h5>
          <div
            v-if="category.emojis.length > 0"
            class="emoji--row"
            :class="{ '!grid !grid-cols-8 !gap-0.5 !p-0': embedded }"
          >
            <button
              v-for="item in category.emojis"
              :key="item.slug"
              type="button"
              v-dompurify-html="item.emoji"
              v-twemoji="{ disabled: progressiveCategories }"
              class="emoji--item"
              :class="{
                '!m-0 !inline-flex !h-[2.375rem] !w-full !items-center !justify-center !rounded-md !text-2xl': embedded,
              }"
              track-by="$index"
              @click="selectEmoji(item.emoji)"
            />
          </div>
        </div>
        <div
          v-if="hasEmptySearchResult"
          class="items-center flex flex-col h-[13.25rem] justify-center"
        >
          <div class="text-n-slate-11 mb-2">
            <FluentIcon icon="emoji" size="48" />
          </div>
          <span class="text-n-slate-11 text-sm font-medium">
            {{ $t('EMOJI.NOT_FOUND') }}
          </span>
        </div>
      </div>
      <div
        v-else-if="hasNoSearch"
        ref="emojiItem"
        class="emoji-item"
        :class="{ '!h-auto !min-h-0 !flex-1 !px-2 !pb-2': embedded }"
      >
        <h5
          class="text-sm text-n-slate-12 font-medium leading-normal m-0 py-1 px-2 capitalize"
          :class="{ '!mt-2': embedded }"
        >
          {{ getCategoryLabel(selectedKey) }}
        </h5>
        <div
          class="emoji--row"
          :class="{ '!grid !grid-cols-8 !gap-0.5 !p-0': embedded }"
        >
          <button
            v-for="item in filterEmojisByCategory"
            :key="item.slug"
            type="button"
            v-dompurify-html="item.emoji"
            v-twemoji
            class="emoji--item"
            :class="{
              '!m-0 !inline-flex !h-[2.375rem] !w-full !items-center !justify-center !rounded-md !text-2xl': embedded,
            }"
            track-by="$index"
            @click="selectEmoji(item.emoji)"
          />
        </div>
      </div>
      <div
        v-else
        ref="emojiItem"
        class="emoji-item"
        :class="{ '!h-auto !min-h-0 !flex-1 !px-2 !pb-2': embedded }"
      >
        <div v-for="category in filterAllEmojisBySearch" :key="category.slug">
          <h5
            v-if="category.emojis.length > 0"
            class="text-sm text-n-slate-12 font-medium leading-normal m-0 py-1 px-2 capitalize"
            :class="{
              '!sticky !top-0 !z-10 !mt-2 !bg-[#f7f8fa] !px-1.5 !py-1.5 !text-[0.6875rem] !font-semibold !uppercase !tracking-wide dark:!bg-n-solid-2': embedded,
            }"
          >
            {{ getCategoryLabel(category.name) }}
          </h5>
          <div
            v-if="category.emojis.length > 0"
            class="emoji--row"
            :class="{ '!grid !grid-cols-8 !gap-0.5 !p-0': embedded }"
          >
            <button
              v-for="item in category.emojis"
              :key="item.slug"
              type="button"
              v-dompurify-html="item.emoji"
              v-twemoji
              class="emoji--item"
              :class="{
                '!m-0 !inline-flex !h-[2.375rem] !w-full !items-center !justify-center !rounded-md !text-2xl': embedded,
              }"
              track-by="$index"
              @click="selectEmoji(item.emoji)"
            />
          </div>
        </div>
        <div
          v-if="hasEmptySearchResult"
          class="items-center flex flex-col h-[13.25rem] justify-center"
        >
          <div class="text-n-slate-11 mb-2">
            <FluentIcon icon="emoji" size="48" />
          </div>
          <span class="text-n-slate-11 text-sm font-medium">
            {{ $t('EMOJI.NOT_FOUND') }}
          </span>
        </div>
      </div>

      <div
        class="emoji-dialog--footer relative w-full py-0 rounded-b-[0.34rem] px-1 bg-n-slate-3"
        :class="{
          '!sticky !top-0 !z-30 !flex-shrink-0 !rounded-none !bg-[#f7f8fa] !px-3 !py-2 dark:!bg-n-solid-2': embedded,
          '!order-first': embedded && !mobileSheet,
          '!order-last !border-t !border-n-weak': mobileSheet,
        }"
        role="menu"
      >
        <ul
          class="flex relative left-[2px] rtl:left-[unset] rtl:right-[2px] list-none m-0 overflow-auto py-1 px-0"
          :class="{ '!left-0 !justify-between !overflow-hidden !py-0': embedded }"
        >
          <li
            v-if="showRecentReactions"
            class="relative"
            :class="{
              '!flex-1 !p-0.5': embedded,
              'emoji-category-active': selectedKey === recentReactionsKey,
            }"
          >
            <button
              class="emoji--item"
              type="button"
              :class="{
                active: selectedKey === recentReactionsKey,
                '!font-bold': selectedKey === recentReactionsKey,
                '!h-[2.125rem] !w-full !justify-center !text-[1.375rem]': embedded,
              }"
              aria-label="Histórico de reações"
              @click="changeCategory(recentReactionsKey)"
            >
              <FluentIcon
                v-if="!embedded"
                icon="history"
                size="16"
                class="text-n-slate-11"
              />
              <i
                v-else
                class="i-lucide-history size-4"
                :class="
                  selectedKey === recentReactionsKey
                    ? activeCategoryIconClass
                    : 'text-n-slate-10'
                "
                aria-hidden="true"
              />
            </button>
            <span
              v-if="selectedKey === recentReactionsKey"
              class="pointer-events-none absolute inset-x-[12%] bottom-0 z-10 h-0.5 rounded-full bg-[#00a884]"
              aria-hidden="true"
            />
          </li>
          <li
            v-for="category in categories"
            :key="category.slug"
            class="relative"
            :class="{
              '!flex-1 !p-0.5': embedded,
              'emoji-category-active': selectedKey === category.name,
            }"
          >
            <button
              class="emoji--item"
              type="button"
              :class="{
                active: selectedKey === category.name,
                '!font-bold': selectedKey === category.name,
                '!h-[2.125rem] !w-full !justify-center !text-[1.375rem]': embedded,
              }"
              :aria-label="getCategoryLabel(category.name)"
              @click="changeCategory(category.name)"
            >
              <i
                v-if="embedded"
                :class="[
                  getCategoryIcon(category.name),
                  'size-5',
                  selectedKey === category.name
                    ? activeCategoryIconClass
                    : 'text-n-slate-10',
                ]"
                aria-hidden="true"
              />
              <span
                v-else
                v-dompurify-html="getFirstEmojiByCategoryName(category.name)"
                v-twemoji
              />
            </button>
            <span
              v-if="selectedKey === category.name"
              class="pointer-events-none absolute inset-x-[12%] bottom-0 z-10 h-0.5 rounded-full bg-[#00a884]"
              aria-hidden="true"
            />
          </li>
        </ul>
      </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
@tailwind components;

@layer components {
  .box-shadow-blue {
    box-shadow:
      0 0 0 1px #1f93ff,
      0 0 1px 2px #c7e3ff;
  }

  .box-shadow-dark {
    box-shadow:
      0 0 0 1px #212222,
      0 0 1px 2px #4c5155;
  }
}
</style>

<style lang="scss">
.emoji-dialog {
  &::before {
    @apply absolute -bottom-3 h-3 w-6 bg-n-slate-3 content-[""];
    clip-path: polygon(50% 100%, 0% 0%, 100% 0%);
  }
}

.emoji--item {
  @apply bg-transparent border-0 rounded cursor-pointer text-lg h-6 m-0 py-0 px-1 hover:bg-n-slate-4;
}

.emoji--row {
  @apply box-border p-1;

  .emoji--item {
    @apply h-[1.625rem] w-[1.625rem] leading-normal m-1;
  }
}

.emoji-item {
  @apply h-[13.25rem] overflow-y-auto;
}

.emoji-dialog--footer {
  ul {
    > li {
      @apply items-center cursor-pointer flex justify-center p-1;
    }

    li .active {
      @apply bg-n-background;
    }

    .emoji--item {
      @apply items-center flex text-sm;

      &:hover {
        @apply bg-n-slate-2;
      }
    }
  }
}

.emoji-dialog--top-tabs {
  @apply flex-shrink-0;
}

.emoji-dialog--top-tab {
  @apply flex-1 flex items-center justify-center gap-1 py-2 px-3 bg-transparent border-0 cursor-pointer text-n-slate-11 text-xs font-medium border-b-2 border-transparent;

  &:hover {
    @apply bg-n-slate-2;
  }
}

.emoji-dialog--top-tab--active {
  @apply text-n-brand border-b-2 border-n-brand;
}

.emoji-dialog--gif-badge {
  @apply font-bold text-sm tracking-wider;
}
</style>
